import { Skeleton } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { formsCompleted } from '#lib/react-query/queries.ts';
import { STAT_WIDGET_CHART_HEIGHT } from '#utils/charts.ts';
import { Base } from '../Base.tsx';
import {
  InfoEmphasis,
  InfoList,
  InfoParagraph,
  InfoSection,
  InfoTemplate,
} from '../InfoTemplateComponents.tsx';
import { useFormWidget } from '../use-form-widget.ts';
import { StatBase } from './StatBase.tsx';

const widgetTitle = 'Forms Completed';

const infoTemplate = (
  <InfoTemplate>
    <InfoSection title="What it is &amp; Problems it Solves">
      <InfoParagraph>
        Shows the total number of forms&nbsp;
        <InfoEmphasis>completed</InfoEmphasis>
        &nbsp;within the selected time period, with a comparison to the previous period of equal
        length. This helps track patient engagement with forms and monitor completion trends over
        time.
      </InfoParagraph>
    </InfoSection>

    <InfoSection title="How it Works (Calculation)" sx={{ mb: 2 }}>
      <InfoSection title="Forms Completed =" indented>
        <InfoList
          items={[
            'Count of all forms completed within the selected date range',
            'Excludes forms that were deleted',
            'Assignment date doesn’t matter — forms may have been assigned earlier but completed during this period',
          ]}
        />
      </InfoSection>

      <InfoSection title="Includes" indented>
        <InfoList
          items={[
            'All forms completed during the selected date range',
            'Forms not deleted',
            'Forms matching applied date and other assessment filters',
          ]}
        />
      </InfoSection>

      <InfoSection title="Excludes" indented>
        <InfoList items={['Forms completed outside the selected date range', 'Deleted forms']} />
      </InfoSection>

      <InfoSection title="Notes &amp; Caveats" indented>
        <InfoList
          items={[
            'Includes both required and optional forms (filters may be applied to view one or the other)',
            'Assignment date does not impact inclusion—only completion date is used',
            'Data updates nightly, so very recent changes may not yet appear.',
          ]}
        />
      </InfoSection>
    </InfoSection>
  </InfoTemplate>
);

export function FormsCompleted() {
  const widget = useFormWidget('forms-completed');

  const {
    minCreated: minCompletedDate,
    maxCreated: maxCompletedDate,
    ...params
  } = widget.getCommonRequestParams();

  const { data, isLoading } = useQuery(
    formsCompleted({
      ...params,
      minCompletedDate,
      maxCompletedDate,
    })
  );

  if (isLoading) {
    return (
      <Base title={widgetTitle}>
        <Skeleton variant="rounded" height={STAT_WIDGET_CHART_HEIGHT} />
      </Base>
    );
  }

  return (
    <StatBase
      data={{
        id: 'forms-completed',
        title: widgetTitle,
        value: data?.value ?? null,
        valueType: 'absolute',
        trend: data?.trend
          ? {
              ...data.trend,
              daysInPeriod: widget.homeLoaderData.totalDaysInRange,
            }
          : undefined,
      }}
      infoTemplate={infoTemplate}
      drilldownRoutePath={widget.getDrilldownRoutePath()}
    />
  );
}
